/**
 * ============================================================================
 *  File        : frontend/src/pages/LoginPage.tsx
 *  Module      : pages
 *  Purpose     : React 登入頁，接 Slice 0 auth endpoints
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-30
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import { LogIn } from 'lucide-react'
import { useState, type FormEvent, type ReactElement } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

interface LoginPageProps {
  t: (key: string) => string
}

export function LoginPage({ t }: LoginPageProps): ReactElement {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  if (auth.isAuthenticated) {
    return <Navigate replace to="/" />
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setLoading(true)
    setError(false)
    try {
      await auth.login(email, password)
      const state = location.state as { from?: { pathname?: string } } | null
      void navigate(state?.from?.pathname ?? '/', { replace: true })
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="login-screen">
      <section className="login-panel">
        <div className="brand-block large">
          <span className="brand-mark">EF</span>
          <div>
            <strong>{t('app.name')}</strong>
            <span>{t('desc.dashboard')}</span>
          </div>
        </div>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            <span>{t('auth.email')}</span>
            <input
              autoComplete="username"
              required
              type="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
          </label>
          <label>
            <span>{t('auth.password')}</span>
            <input
              autoComplete="current-password"
              required
              type="password"
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
          </label>
          <button className="icon-button primary wide" disabled={loading} type="submit">
            <LogIn aria-hidden="true" size={18} />
            <span>{loading ? t('auth.loading') : t('auth.signIn')}</span>
          </button>
          {error ? <p className="error-text">{t('auth.error')}</p> : null}
        </form>
      </section>
    </main>
  )
}
